/**
 * useValidationFixes Hook
 *
 * FERP CRITICAL: React hook for LLM Fix Assistant operations
 * Manages AI-powered validation fix recommendations with human-in-the-loop
 *
 * Principles:
 * - Repository pattern (all calls through LLMFixRepository)
 * - Type-safe operations with explicit error handling
 * - Mantine notifications for user feedback
 * - Complete audit trail integration
 * - No automatic fixes (human approval required)
 *
 * Usage:
 * ```tsx
 * const { recommendation, loading, requestFixes, applyFixes, rejectFixes } = useValidationFixes(templateId);
 *
 * // Request AI fix recommendations
 * await requestFixes(templateId);
 *
 * // Apply selected fixes (re-validation automatic)
 * await applyFixes(recommendationId, [0, 2, 5]); // Apply fixes at indices 0, 2, 5
 *
 * // Or apply all fixes
 * await applyAllFixes(recommendationId);
 *
 * // Reject recommendations
 * await rejectFixes(recommendationId, 'Fixes would break domain logic');
 * ```
 */

import { useState, useCallback } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { notifications } from '@mantine/notifications';
import { useCurrentTenant } from './useCurrentTenant';
import { useAuth } from '../security/useAuth';
import { createLLMFixRepository } from '../data/repositories/LLMFixRepository';
import type {
  LLMFixRecommendation,
  ApplyFixResult,
} from '../graphql/llmFixOperations';

// ============================================================================
// HOOK INTERFACE
// ============================================================================

interface UseValidationFixesResult {
  // State
  recommendation: LLMFixRecommendation | null;
  loading: boolean;
  error: string | null;
  applyResult: ApplyFixResult | null;

  // Actions
  requestFixes: (templateId: string) => Promise<void>;
  applyFixes: (recommendationId: string, fixIndices?: number[]) => Promise<void>;
  applyAllFixes: (recommendationId: string) => Promise<void>;
  rejectFixes: (recommendationId: string, reason?: string) => Promise<void>;
  clearResult: () => void;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

export function useValidationFixes(templateId: string): UseValidationFixesResult {
  const client = useApolloClient();
  const { currentTenant } = useCurrentTenant();
  const { userId } = useAuth();

  const [recommendation, setRecommendation] = useState<LLMFixRecommendation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applyResult, setApplyResult] = useState<ApplyFixResult | null>(null);

  const repository = createLLMFixRepository(client);

  /**
   * Request AI fix recommendations for validation errors
   *
   * Flow:
   * 1. Call backend requestLLMFix mutation
   * 2. Backend runs Phase 12 validation
   * 3. Backend calls Claude API for fix recommendations
   * 4. Backend stores recommendation in DynamoDB
   * 5. Frontend displays recommendations with confidence scores
   */
  const requestFixes = useCallback(
    async (templateId: string) => {
      if (!currentTenant || !userId) {
        setError('Missing tenant or user information');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        notifications.show({
          id: 'request-fix',
          title: 'Analyzing Template',
          message: 'AI is analyzing validation errors and generating fix recommendations...',
          loading: true,
          autoClose: false,
        });

        const result = await repository.requestFix(
          templateId,
          currentTenant.id,
          userId
        );

        setRecommendation(result);

        notifications.update({
          id: 'request-fix',
          title: 'Fix Recommendations Ready',
          message: `Found ${result.suggested_fixes.length} recommended fixes (${Math.round(result.confidence * 100)}% confidence)`,
          color: 'green',
          loading: false,
          autoClose: 3000,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to generate fix recommendations';
        setError(message);

        notifications.update({
          id: 'request-fix',
          title: 'Fix Generation Failed',
          message,
          color: 'red',
          loading: false,
          autoClose: 5000,
        });
      } finally {
        setLoading(false);
      }
    },
    [repository, currentTenant, userId]
  );

  /**
   * Apply selected fixes and re-validate template
   *
   * Flow:
   * 1. Call backend applyLLMFix mutation
   * 2. Backend applies fixes to template
   * 3. Backend runs Phase 12 re-validation
   * 4. Backend updates DynamoDB with results
   * 5. Frontend displays validation results
   *
   * @param recommendationId - Backend Snowflake ID of recommendation
   * @param fixIndices - Optional array of fix indices to apply (if undefined, applies all)
   */
  const applyFixes = useCallback(
    async (recommendationId: string, fixIndices?: number[]) => {
      if (!currentTenant || !userId) {
        setError('Missing tenant or user information');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        notifications.show({
          id: 'apply-fix',
          title: 'Applying Fixes',
          message: 'Applying selected fixes and re-validating template...',
          loading: true,
          autoClose: false,
        });

        const result = await repository.applyFix(
          recommendationId,
          templateId,
          currentTenant.id,
          userId,
          fixIndices
        );

        setApplyResult(result);

        if (result.validation_passed) {
          notifications.update({
            id: 'apply-fix',
            title: 'Fixes Applied Successfully',
            message: `Applied ${result.fixes_applied} fixes. Template is now valid!`,
            color: 'green',
            loading: false,
            autoClose: 3000,
          });
        } else {
          notifications.update({
            id: 'apply-fix',
            title: 'Fixes Applied, But Validation Still Failing',
            message: `Applied ${result.fixes_applied} fixes, but ${result.remaining_violations.length} errors remain`,
            color: 'yellow',
            loading: false,
            autoClose: 5000,
          });
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to apply fixes';
        setError(message);

        notifications.update({
          id: 'apply-fix',
          title: 'Fix Application Failed',
          message,
          color: 'red',
          loading: false,
          autoClose: 5000,
        });
      } finally {
        setLoading(false);
      }
    },
    [repository, templateId, currentTenant, userId]
  );

  /**
   * Apply all fixes (convenience method)
   */
  const applyAllFixes = useCallback(
    async (recommendationId: string) => {
      await applyFixes(recommendationId); // No fixIndices = apply all
    },
    [applyFixes]
  );

  /**
   * Reject fix recommendations
   *
   * Flow:
   * 1. Call backend rejectLLMFix mutation
   * 2. Backend updates DynamoDB status to Rejected
   * 3. Backend records audit trail (who/when/why)
   * 4. Frontend clears recommendation state
   *
   * @param recommendationId - Backend Snowflake ID of recommendation
   * @param reason - Optional rejection reason (for audit trail)
   */
  const rejectFixes = useCallback(
    async (recommendationId: string, reason?: string) => {
      if (!currentTenant) {
        setError('Missing tenant information');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        await repository.rejectFix(
          recommendationId,
          templateId,
          currentTenant.id,
          reason
        );

        notifications.show({
          title: 'Fixes Rejected',
          message: 'Fix recommendations have been rejected and archived',
          color: 'blue',
          autoClose: 3000,
        });

        setRecommendation(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to reject fixes';
        setError(message);

        notifications.show({
          title: 'Rejection Failed',
          message,
          color: 'red',
          autoClose: 5000,
        });
      } finally {
        setLoading(false);
      }
    },
    [repository, templateId, currentTenant]
  );

  /**
   * Clear result state (for resetting UI)
   */
  const clearResult = useCallback(() => {
    setApplyResult(null);
    setRecommendation(null);
    setError(null);
  }, []);

  return {
    recommendation,
    loading,
    error,
    applyResult,
    requestFixes,
    applyFixes,
    applyAllFixes,
    rejectFixes,
    clearResult,
  };
}

export default useValidationFixes;
